import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import { useDialogRef, dialogPanelProps } from '../../components/a11y';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * Org email templates — HTML templates for share emails, imported as a
 * zip (index.html + images/). Preview and delete are new: classic's
 * list was name-only with no actions, its GET-by-id route was doubly
 * broken (inverted permission check + wrong lookup column), and no
 * delete endpoint existed — both fixed cloud-side with this page.
 */

interface TemplateRow {
    id: number;
    name: string;
    body?: string;
    createdAt?: string;
}

export default function OrgEmailTemplatesV2() {
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();

    const [rows, setRows] = useState<TemplateRow[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [preview, setPreview] = useState<TemplateRow | null>(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importName, setImportName] = useState('');
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState<string | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const previewRef = useDialogRef(preview != null, () =>
        setPreview(null),
    );
    const importRef = useDialogRef(importOpen, () => setImportOpen(false));

    const load = async () => {
        const data = await authenticatedApi
            .get(`${orgId}/templates`)
            .then((r) => r.data ?? [])
            .catch(() => []);
        setRows(data);
        setIsLoading(false);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    const submitImport = async () => {
        if (!importName.trim() || !importFile || importing) return;
        setImporting(true);
        setImportError(null);
        const formData = new FormData();
        formData.append('zip', importFile);
        formData.append('name', importName.trim());
        formData.append('orgId', String(orgId));
        const ok = await authenticatedApi
            .post(`${orgId}/templates/import`, formData)
            .then(() => true)
            .catch(() => false);
        setImporting(false);
        if (ok) {
            setImportOpen(false);
            setImportName('');
            setImportFile(null);
            toast.success('Template imported');
            load();
        } else {
            setImportError(
                'Import failed — check the zip has index.html at its root and the name is unique.',
            );
        }
    };

    const remove = async (row: TemplateRow) => {
        if (
            !window.confirm(
                `Delete the "${row.name}" template? Galleries using it fall back to the default email.`,
            )
        )
            return;
        const prev = rows;
        setRows((current) => current.filter((r) => r.id !== row.id));
        const ok = await authenticatedApi
            .delete(`${orgId}/templates/${row.id}`)
            .then(() => true)
            .catch(() => false);
        if (!ok) {
            setRows(prev);
            toast.error("Couldn't delete the template.");
        }
    };

    if (isLoading) {
        return (
            <div className="mb-6 h-40 animate-pulse rounded-[16px] bg-secondary" />
        );
    }

    return (
        <>
            <SettingsCard
                title="Email templates"
                desc="HTML templates for share emails — a zip with index.html plus an images/ folder."
                foot={
                    <>
                        <span className="flex-1 text-[11.5px] text-faint">
                            {rows.length} template
                            {rows.length === 1 ? '' : 's'}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                setImportError(null);
                                setImportOpen(true);
                            }}
                            className="cursor-pointer rounded-full border-0 bg-foreground px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-background"
                        >
                            Import template
                        </button>
                    </>
                }
            >
                {rows.length === 0 && (
                    <p className="py-2 text-[13px] text-muted-foreground">
                        No templates yet — share emails use the default.
                    </p>
                )}
                {rows.map((row) => (
                    <div
                        key={row.id}
                        className="group flex items-center gap-3 border-b border-line py-[9px] text-[12.5px] last:border-b-0"
                    >
                        <span className="min-w-0 flex-1 truncate font-semibold">
                            {row.name}
                        </span>
                        {row.createdAt && (
                            <span className="whitespace-nowrap text-[11.5px] text-faint">
                                imported{' '}
                                {moment(row.createdAt).format('MMM D')}
                            </span>
                        )}
                        <span className="flex gap-3 text-[12px] font-semibold opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
                            <button
                                type="button"
                                onClick={() => setPreview(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-muted-foreground hover:text-foreground"
                            >
                                preview
                            </button>
                            <button
                                type="button"
                                title="Delete template"
                                onClick={() => remove(row)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-faint hover:text-attention"
                            >
                                ×
                            </button>
                        </span>
                    </div>
                ))}
            </SettingsCard>

            {preview && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setPreview(null)}
                >
                    <div
                        ref={previewRef}
                        {...dialogPanelProps(`Preview — ${preview.name}`)}
                        className="flex h-[min(80vh,720px)] w-full max-w-[640px] flex-col overflow-hidden rounded-[15px] border border-border bg-background shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 border-b border-line px-5 py-3">
                            <span className="ps-label">
                                {preview.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPreview(null)}
                                className="ml-auto cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[13px] text-faint hover:text-foreground"
                            >
                                ×
                            </button>
                        </div>
                        {/* sandboxed — template HTML runs no scripts */}
                        <iframe
                            title={`Email template preview: ${preview.name}`}
                            sandbox=""
                            srcDoc={preview.body ?? ''}
                            className="w-full flex-1 border-0 bg-white"
                        />
                    </div>
                </div>
            )}

            {importOpen && (
                <div
                    className="psv2 fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
                    onClick={() => setImportOpen(false)}
                >
                    <div
                        ref={importRef}
                        {...dialogPanelProps('Import template')}
                        className="w-full max-w-[400px] rounded-[15px] border border-border bg-background p-5 shadow-xl outline-none"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="ps-label mb-3.5">
                            Import template
                        </div>
                        <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                            Name
                        </label>
                        <input
                            autoFocus
                            className="ps-input mb-3 w-full text-[13px]"
                            placeholder="Holiday Party 2026"
                            value={importName}
                            onChange={(e) => setImportName(e.target.value)}
                        />
                        <label className="mb-1.5 block text-[12px] font-semibold text-muted-foreground">
                            Template zip
                        </label>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".zip,application/zip"
                            className="mb-1.5 block w-full text-[12.5px] text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-full file:border file:border-border file:bg-transparent file:px-3.5 file:py-1.5 file:font-[inherit] file:text-[12px] file:font-semibold file:text-foreground"
                            onChange={(e) =>
                                setImportFile(e.target.files?.[0] ?? null)
                            }
                        />
                        <p className="mb-4 text-[11px] text-faint">
                            index.html at the root, images in images/.
                        </p>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                disabled={
                                    !importName.trim() ||
                                    !importFile ||
                                    importing
                                }
                                onClick={submitImport}
                                className="cursor-pointer rounded-full border-0 bg-brand px-5 py-2 font-[inherit] text-[13px] font-semibold text-brand-foreground disabled:opacity-50"
                            >
                                {importing ? 'Importing…' : 'Import'}
                            </button>
                            <button
                                type="button"
                                onClick={() => setImportOpen(false)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-muted-foreground"
                            >
                                Cancel
                            </button>
                        </div>
                        {importError && (
                            <p className="mt-2.5 text-[12px] font-semibold text-attention">
                                {importError}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
